import {
  BUDGET_RULE_MAX_INCREASE_PERCENT,
  BUDGET_RULE_MAX_THRESHOLD,
  type BudgetRule,
  type BudgetRuleInput,
  type BudgetRuleMetric,
  type BudgetRuleOperator,
  budgetRuleInputSchema,
  type DayOfWeek,
} from "@adpilot/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Radio, RadioGroup } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { getApiErrorCode, getApiErrorMessage } from "@/lib/api-error";
import {
  createBudgetRule,
  fetchRecommendedRuleEvents,
  updateBudgetRule,
} from "@/lib/budget-rules-api";
import { useFormatters } from "@/lib/format";

const DAYS: DayOfWeek[] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];
const METRICS: BudgetRuleMetric[] = ["acos", "roas", "ctr", "cvr"];
const OPERATORS: BudgetRuleOperator[] = ["gt", "gte", "lt", "lte", "eq"];

interface BudgetRuleDialogProps {
  campaignId: string;
  /** Current daily budget, for the 「活动预算将设置为」 preview; null hides it. */
  dailyBudget: number | null;
  currency: string;
  /** Edit an existing rule; omitted = create. */
  rule?: BudgetRule;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface FieldErrors {
  name?: string;
  eventId?: string;
  startDate?: string;
  endDate?: string;
  days?: string;
  increase?: string;
  threshold?: string;
}

function RequiredMark() {
  return (
    <span aria-hidden="true" className="text-destructive-foreground">
      *
    </span>
  );
}

/**
 * ADP-222: create / edit an Amazon SP budget rule for one campaign. Mirrors the
 * reference form: name, Schedule vs Performance, recommended event or custom
 * dates, daily or weekly recurrence, and a 0–1000% increase with a preview of
 * the budget the campaign will get.
 */
export function BudgetRuleDialog(props: BudgetRuleDialogProps) {
  // Remount per open/rule so the form always starts from the rule being edited.
  return props.open ? <BudgetRuleForm key={props.rule?.id ?? "new"} {...props} /> : null;
}

function BudgetRuleForm({
  campaignId,
  dailyBudget,
  currency,
  rule,
  open,
  onOpenChange,
}: BudgetRuleDialogProps) {
  const { t } = useTranslation("campaign");
  const { formatCurrency, formatDate, formatNumber } = useFormatters();
  const queryClient = useQueryClient();

  const [name, setName] = useState(rule?.name ?? "");
  const [ruleType, setRuleType] = useState(rule?.ruleType ?? "schedule");
  const [durationType, setDurationType] = useState(rule?.duration.type ?? "date_range");
  const [eventId, setEventId] = useState<string | null>(
    rule?.duration.type === "event" ? rule.duration.eventId : null,
  );
  const [startDate, setStartDate] = useState(
    rule?.duration.type === "date_range" ? rule.duration.startDate : "",
  );
  const [endDate, setEndDate] = useState(
    rule?.duration.type === "date_range" ? (rule.duration.endDate ?? "") : "",
  );
  const [recurrenceType, setRecurrenceType] = useState(rule?.recurrence.type ?? "daily");
  const [days, setDays] = useState<DayOfWeek[]>(
    rule?.recurrence.type === "weekly" ? rule.recurrence.daysOfWeek : [],
  );
  const [increase, setIncrease] = useState(rule ? String(rule.increasePercent) : "");
  const [metric, setMetric] = useState<BudgetRuleMetric>(rule?.performance?.metric ?? "acos");
  const [operator, setOperator] = useState<BudgetRuleOperator>(rule?.performance?.operator ?? "lt");
  const [threshold, setThreshold] = useState(
    rule?.performance ? String(rule.performance.threshold) : "",
  );
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const events = useQuery({
    queryKey: ["campaign", campaignId, "budget-rule-events"],
    queryFn: () => fetchRecommendedRuleEvents(campaignId),
    enabled: open && durationType === "event",
    staleTime: 10 * 60_000,
  });
  // The rule's own event stays selectable even when Amazon no longer recommends it.
  const currentEvent =
    rule?.duration.type === "event"
      ? {
          eventId: rule.duration.eventId,
          eventName: rule.duration.eventName,
          startDate: rule.duration.startDate,
          endDate: rule.duration.endDate,
          suggestedIncreasePercent: null,
        }
      : null;
  const eventOptions = [
    ...(currentEvent && !events.data?.some((event) => event.eventId === currentEvent.eventId)
      ? [currentEvent]
      : []),
    ...(events.data ?? []),
  ];
  const selectedEvent = eventOptions.find((event) => event.eventId === eventId) ?? null;

  const mutation = useMutation({
    mutationFn: (input: BudgetRuleInput) =>
      rule ? updateBudgetRule(campaignId, rule.id, input) : createBudgetRule(campaignId, input),
  });

  function buildInput(): BudgetRuleInput | null {
    const next: FieldErrors = {};
    if (!name.trim()) next.name = t("detail.budget.rules.errors.name");
    if (durationType === "event" && !eventId) next.eventId = t("detail.budget.rules.errors.event");
    if (durationType === "date_range") {
      if (!startDate) next.startDate = t("detail.budget.rules.errors.startDate");
      if (startDate && endDate && endDate < startDate) {
        next.endDate = t("detail.budget.rules.errors.endBeforeStart");
      }
    }
    if (recurrenceType === "weekly" && days.length === 0) {
      next.days = t("detail.budget.rules.errors.days");
    }
    const pct = Number(increase);
    if (!increase || Number.isNaN(pct) || pct <= 0 || pct > BUDGET_RULE_MAX_INCREASE_PERCENT) {
      next.increase = t("detail.budget.rules.errors.increase", {
        max: BUDGET_RULE_MAX_INCREASE_PERCENT,
      });
    }
    const limit = Number(threshold);
    if (
      ruleType === "performance" &&
      (!threshold || Number.isNaN(limit) || limit < 0 || limit > BUDGET_RULE_MAX_THRESHOLD)
    ) {
      next.threshold = t("detail.budget.rules.errors.threshold", {
        max: formatNumber(BUDGET_RULE_MAX_THRESHOLD),
      });
    }
    setErrors(next);
    if (Object.keys(next).length > 0) return null;

    const parsed = budgetRuleInputSchema.safeParse({
      name: name.trim(),
      ruleType,
      duration:
        durationType === "event"
          ? {
              type: "event",
              eventId,
              eventName: selectedEvent?.eventName ?? null,
              startDate: selectedEvent?.startDate ?? null,
              endDate: selectedEvent?.endDate ?? null,
            }
          : { type: "date_range", startDate, endDate: endDate || null },
      recurrence:
        recurrenceType === "weekly" ? { type: "weekly", daysOfWeek: days } : { type: "daily" },
      increasePercent: Math.round(pct * 100) / 100,
      performance: ruleType === "performance" ? { metric, operator, threshold: limit } : null,
    });
    if (!parsed.success) {
      setSubmitError(t("detail.budget.rules.errors.fallback"));
      return null;
    }
    return parsed.data;
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitError(null);
    const input = buildInput();
    if (!input) return;
    try {
      await mutation.mutateAsync(input);
      toast.success(t(rule ? "detail.budget.rules.updated" : "detail.budget.rules.created"));
      void queryClient.invalidateQueries({ queryKey: ["campaign", campaignId, "budget-rules"] });
      onOpenChange(false);
    } catch (error) {
      // A created-but-unattached rule still changed Amazon: refresh either way.
      void queryClient.invalidateQueries({ queryKey: ["campaign", campaignId, "budget-rules"] });
      const message = await getApiErrorMessage(error);
      const code = await getApiErrorCode(error);
      // Amazon may already hold the rule: keeping the form open invites a second
      // Create (a duplicate rule). Close and let the list show what exists.
      if (
        code === "campaign.budget_rule.attach_failed" ||
        code === "campaign.budget_rule.result_unknown"
      ) {
        toast.error(message);
        onOpenChange(false);
        return;
      }
      setSubmitError(message);
    }
  }

  const pct = Number(increase);
  const boosted =
    dailyBudget != null && pct > 0 && !Number.isNaN(pct) ? dailyBudget * (1 + pct / 100) : null;
  const period =
    durationType === "event"
      ? (selectedEvent?.eventName ?? null)
      : startDate
        ? endDate
          ? `${formatDate(startDate)} – ${formatDate(endDate)}`
          : t("detail.budget.rules.fromDate", { date: formatDate(startDate) })
        : null;

  const eventItems = [
    { value: null, label: t("detail.budget.rules.eventPlaceholder") },
    ...eventOptions.map((item) => ({
      value: item.eventId,
      label: item.eventName ?? item.eventId,
    })),
  ];
  const metricItems = METRICS.map((value) => ({
    value,
    label: t(`detail.budget.rules.metric.${value}`),
  }));
  const operatorItems = OPERATORS.map((value) => ({
    value,
    label: t(`detail.budget.rules.operator.${value}`),
  }));

  return (
    <Dialog open={open} onOpenChange={(next) => !mutation.isPending && onOpenChange(next)}>
      <DialogPopup>
        <DialogHeader>
          <DialogTitle>
            {t(rule ? "detail.budget.rules.editTitle" : "detail.budget.rules.createTitle")}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} noValidate className="contents">
          <DialogPanel className="flex flex-col gap-4">
            {rule && rule.campaignCount > 1 ? (
              <p className="rounded-md bg-warning/10 px-3 py-2 text-[12px] leading-4 text-ink">
                {t("detail.budget.rules.sharedEditWarning", { count: rule.campaignCount })}
              </p>
            ) : null}
            <Field invalid={errors.name != null}>
              <FieldLabel htmlFor="budget-rule-name">
                <RequiredMark />
                {t("detail.budget.rules.name")}
              </FieldLabel>
              <Input
                id="budget-rule-name"
                value={name}
                maxLength={255}
                onChange={(event) => setName(event.target.value)}
                aria-invalid={errors.name != null}
              />
              {errors.name ? <FieldError match>{errors.name}</FieldError> : null}
            </Field>

            <ChoiceField
              label={t("detail.budget.rules.type")}
              name="budget-rule-type"
              value={ruleType}
              options={(["schedule", "performance"] as const).map((value) => ({
                value,
                label: t(`detail.budget.rules.ruleType.${value}`),
              }))}
              onChange={setRuleType}
            />

            <ChoiceField
              label={t("detail.budget.rules.dateRange")}
              name="budget-rule-duration"
              value={durationType}
              options={(["event", "date_range"] as const).map((value) => ({
                value,
                label: t(`detail.budget.rules.durationType.${value}`),
              }))}
              onChange={setDurationType}
            />

            {durationType === "event" ? (
              <Field invalid={errors.eventId != null}>
                <FieldLabel>
                  <RequiredMark />
                  {t("detail.budget.rules.event")}
                </FieldLabel>
                <Select
                  items={eventItems}
                  value={eventId}
                  onValueChange={(value) => setEventId(value)}
                  disabled={events.isLoading}
                >
                  <SelectTrigger className="w-full" aria-invalid={errors.eventId != null}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {eventItems.map((item) => (
                        <SelectItem key={item.value ?? "empty"} value={item.value}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                {events.isError ? (
                  <FieldError match>{t("detail.budget.rules.eventsError")}</FieldError>
                ) : !events.isLoading && (events.data?.length ?? 0) === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    {t("detail.budget.rules.noEvents")}
                  </p>
                ) : null}
                {selectedEvent?.suggestedIncreasePercent != null ? (
                  <p className="text-xs text-muted-foreground">
                    {t("detail.budget.rules.suggestedIncrease", {
                      pct: selectedEvent.suggestedIncreasePercent,
                    })}
                  </p>
                ) : null}
                {errors.eventId ? <FieldError match>{errors.eventId}</FieldError> : null}
              </Field>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <Field invalid={errors.startDate != null}>
                  <FieldLabel htmlFor="budget-rule-start">
                    <RequiredMark />
                    {t("detail.budget.rules.startDate")}
                  </FieldLabel>
                  <Input
                    id="budget-rule-start"
                    type="date"
                    value={startDate}
                    onChange={(event) => setStartDate(event.target.value)}
                    aria-invalid={errors.startDate != null}
                  />
                  {errors.startDate ? <FieldError match>{errors.startDate}</FieldError> : null}
                </Field>
                <Field invalid={errors.endDate != null}>
                  <FieldLabel htmlFor="budget-rule-end">
                    {t("detail.budget.rules.endDate")}
                  </FieldLabel>
                  <Input
                    id="budget-rule-end"
                    type="date"
                    value={endDate}
                    min={startDate || undefined}
                    onChange={(event) => setEndDate(event.target.value)}
                    aria-invalid={errors.endDate != null}
                  />
                  {errors.endDate ? <FieldError match>{errors.endDate}</FieldError> : null}
                </Field>
              </div>
            )}

            <ChoiceField
              label={t("detail.budget.rules.recurrence")}
              name="budget-rule-recurrence"
              value={recurrenceType}
              options={(["daily", "weekly"] as const).map((value) => ({
                value,
                label: t(`detail.budget.rules.recurrenceType.${value}`),
              }))}
              onChange={setRecurrenceType}
            />
            {recurrenceType === "weekly" ? (
              <Field invalid={errors.days != null}>
                <ToggleGroup
                  multiple
                  value={days}
                  onValueChange={(values) => setDays(values as DayOfWeek[])}
                  aria-label={t("detail.budget.rules.daysOfWeek")}
                  className="flex flex-wrap gap-1"
                >
                  {DAYS.map((day) => (
                    <ToggleGroupItem key={day} value={day} variant="outline" size="sm">
                      {t(`detail.budget.rules.day.${day}`)}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
                {errors.days ? <FieldError match>{errors.days}</FieldError> : null}
              </Field>
            ) : null}

            {ruleType === "performance" ? (
              <Field invalid={errors.threshold != null}>
                <FieldLabel>
                  <RequiredMark />
                  {t("detail.budget.rules.condition")}
                </FieldLabel>
                <div className="grid grid-cols-[1fr_1.3fr_1fr] gap-2">
                  <Select
                    items={metricItems}
                    value={metric}
                    onValueChange={(v) => v && setMetric(v)}
                  >
                    <SelectTrigger aria-label={t("detail.budget.rules.metricLabel")}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        {metricItems.map((item) => (
                          <SelectItem key={item.value} value={item.value}>
                            {item.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <Select
                    items={operatorItems}
                    value={operator}
                    onValueChange={(v) => v && setOperator(v)}
                  >
                    <SelectTrigger aria-label={t("detail.budget.rules.operatorLabel")}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        {operatorItems.map((item) => (
                          <SelectItem key={item.value} value={item.value}>
                            {item.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step={0.01}
                    value={threshold}
                    onChange={(event) => setThreshold(event.target.value)}
                    aria-label={t("detail.budget.rules.thresholdLabel")}
                    aria-invalid={errors.threshold != null}
                  />
                </div>
                {errors.threshold ? <FieldError match>{errors.threshold}</FieldError> : null}
              </Field>
            ) : null}

            <Field invalid={errors.increase != null}>
              <FieldLabel htmlFor="budget-rule-increase">
                <RequiredMark />
                {t("detail.budget.rules.increase")}
              </FieldLabel>
              <div className="flex items-center gap-2">
                <Input
                  id="budget-rule-increase"
                  type="number"
                  inputMode="decimal"
                  min={0.01}
                  max={BUDGET_RULE_MAX_INCREASE_PERCENT}
                  step={0.01}
                  value={increase}
                  onChange={(event) => setIncrease(event.target.value)}
                  aria-invalid={errors.increase != null}
                />
                <span className="text-muted-foreground">%</span>
              </div>
              {errors.increase ? <FieldError match>{errors.increase}</FieldError> : null}
            </Field>

            {boosted != null ? (
              <p className="rounded-md bg-subtle px-3 py-2 text-[12px] leading-4 text-ink">
                {t("detail.budget.rules.preview", {
                  period: period ?? t("detail.budget.rules.previewPeriodTbd"),
                  budget: formatCurrency(boosted, currency),
                })}
                {ruleType === "performance" && threshold !== ""
                  ? ` ${t("detail.budget.rules.previewCondition", {
                      condition: t("detail.budget.rules.conditionSummary", {
                        metric: t(`detail.budget.rules.metric.${metric}`),
                        operator: t(`detail.budget.rules.operator.${operator}`),
                        threshold,
                      }),
                    })}`
                  : null}
              </p>
            ) : null}

            {submitError ? (
              <p role="alert" className="text-[12px] leading-4 text-destructive-foreground">
                {submitError}
              </p>
            ) : null}
          </DialogPanel>
          <DialogFooter>
            <DialogClose
              render={<Button variant="outline" type="button" disabled={mutation.isPending} />}
            >
              {t("common:actions.cancel")}
            </DialogClose>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? <Spinner /> : null}
              {t(rule ? "detail.budget.rules.save" : "detail.budget.rules.create")}
            </Button>
          </DialogFooter>
        </form>
      </DialogPopup>
    </Dialog>
  );
}

function ChoiceField<T extends string>({
  label,
  name,
  value,
  options,
  onChange,
}: {
  label: string;
  name: string;
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T) => void;
}) {
  return (
    <Field>
      <FieldLabel id={`${name}-label`}>{label}</FieldLabel>
      <RadioGroup
        aria-labelledby={`${name}-label`}
        className="flex flex-wrap gap-4"
        value={value}
        onValueChange={(next) => {
          const match = options.find((option) => option.value === next);
          if (match) onChange(match.value);
        }}
      >
        {options.map((option) => (
          <label
            key={option.value}
            htmlFor={`${name}-${option.value}`}
            className="flex cursor-pointer items-center gap-2 text-[13px]"
          >
            <Radio id={`${name}-${option.value}`} value={option.value} />
            {option.label}
          </label>
        ))}
      </RadioGroup>
    </Field>
  );
}
